import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiClient } from '@/lib/api-client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Users, Network, Activity } from 'lucide-react'

interface PlatformStats {
  totalUsers: number
  activeToday: number
  totalNetworks: number
  totalValidations: number
  usersByPlan: Record<string, number>
  usersByRole: Record<string, number>
}

interface AuditLogEntry {
  id: string
  userId: string
  action: string
  resource: string
  success: boolean
  createdAt: string
}

export default function AdminDashboardPage() {
  const [stats, setStats] = useState<PlatformStats | null>(null)
  const [recentActivity, setRecentActivity] = useState<AuditLogEntry[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      const [statsResp, activityResp] = await Promise.all([
        apiClient.getAdminStats(),
        apiClient.listAuditLogs({ limit: 10 }),
      ])
      if (statsResp.success && statsResp.data) {
        setStats(statsResp.data as PlatformStats)
      }
      if (activityResp.success && activityResp.data) {
        setRecentActivity((activityResp.data as { auditLogs: AuditLogEntry[] }).auditLogs || [])
      }
      setLoading(false)
    }
    load()
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    )
  }

  const statCards = [
    { label: 'Total Users', value: stats?.totalUsers ?? 0, icon: Users },
    { label: 'Validations (24h)', value: stats?.activeToday ?? 0, icon: Activity },
    { label: 'Networks', value: stats?.totalNetworks ?? 0, icon: Network },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Admin Dashboard</h1>
        <p className="text-sm text-muted-foreground">Platform overview and recent activity</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((card) => (
          <Card key={card.label} className="app-surface">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{card.label}</CardTitle>
              <card.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold tabular-nums">{card.value.toLocaleString()}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="app-surface">
          <CardHeader>
            <CardTitle className="text-base">Users by Plan</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.usersByPlan && Object.entries(stats.usersByPlan).map(([plan, count]) => (
                <div key={plan} className="flex items-center justify-between">
                  <Badge variant="outline" className="capitalize">{plan}</Badge>
                  <span className="text-sm font-medium tabular-nums">{count}</span>
                </div>
              ))}
              {(!stats?.usersByPlan || Object.keys(stats.usersByPlan).length === 0) && (
                <p className="text-sm text-muted-foreground">No data</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="app-surface">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Recent Activity</CardTitle>
            <Link to="/admin/audit-logs" className="text-xs text-primary hover:underline">View all</Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentActivity.map((entry) => (
                <div key={entry.id} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <Badge variant={entry.success ? 'default' : 'destructive'} className="text-[10px] px-1.5">
                      {entry.success ? 'OK' : 'FAIL'}
                    </Badge>
                    <span className="font-mono text-xs">{entry.action}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {new Date(entry.createdAt).toLocaleDateString()}
                  </span>
                </div>
              ))}
              {recentActivity.length === 0 && (
                <p className="text-sm text-muted-foreground">No recent activity</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
